<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import type { AvailableModel } from '@/types/generated'
  import { EventNameEnum } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useMetrics } from '@/composables/useMetrics'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Check } from 'lucide-vue-next'
  import chatgptLogo from '@/assets/icons/chatgpt-logo.png'
  import anthropicLogo from '@/assets/icons/anthropic-logo.svg'

  // Brand-name version strings rendered as a subtitle under each option's
  // display_label. Hardcoded because model-version strings don't translate
  // and rarely change. Move to AIModelConfiguration if admins need to edit
  // these without a deploy.
  const MODEL_VERSION_BY_ID: Record<string, string> = {
    'gpt-5.4': 'GPT-5.4',
    'gpt-5.4-mini': 'GPT-5.4-mini',
    'gpt-5.5': 'GPT-5.5',
    'eu.anthropic.claude-opus-4-7': 'Opus 4.7',
    'eu.anthropic.claude-opus-4-6-v1': 'Opus 4.6',
    'eu.anthropic.claude-opus-4-5-20251101-v1:0': 'Opus 4.5',
    'eu.anthropic.claude-sonnet-4-6': 'Sonnet 4.6'
  }

  const getVersion = (modelId: string): string => MODEL_VERSION_BY_ID[modelId] ?? ''

  const { t } = useTranslation({
    en: {
      auto: 'Auto',
      autoDesc: 'Recommended for most chats',
      'gpt-5.4': 'Highest quality for difficult questions',
      'gpt-5.4-mini': 'Fast answers for everyday use',
      'gpt-5.5': 'Highest quality for difficult questions',
      'eu.anthropic.claude-opus-4-7': 'Great for deep analysis and long files',
      'eu.anthropic.claude-opus-4-6-v1': 'Great for deep analysis and long files',
      'eu.anthropic.claude-opus-4-5-20251101-v1:0': 'Great for deep analysis and long files',
      'eu.anthropic.claude-sonnet-4-6': 'Great for writing, summaries, and edits'
    },
    de: {
      auto: 'Auto',
      autoDesc: 'Empfohlen für die meisten Chats',
      'gpt-5.4': 'Höchste Qualität für schwierige Fragen',
      'gpt-5.4-mini': 'Schnelle Antworten für den Alltag',
      'gpt-5.5': 'Höchste Qualität für schwierige Fragen',
      'eu.anthropic.claude-opus-4-7': 'Ideal für tiefe Analysen und lange Dateien',
      'eu.anthropic.claude-opus-4-6-v1': 'Ideal für tiefe Analysen und lange Dateien',
      'eu.anthropic.claude-opus-4-5-20251101-v1:0': 'Ideal für tiefe Analysen und lange Dateien',
      'eu.anthropic.claude-sonnet-4-6': 'Ideal für Texte, Zusammenfassungen und Bearbeitungen'
    },
    fr: {
      auto: 'Auto',
      autoDesc: 'Recommandé pour la plupart des chats',
      'gpt-5.4': 'Qualité maximale pour les questions difficiles',
      'gpt-5.4-mini': 'Réponses rapides pour un usage quotidien',
      'gpt-5.5': 'Qualité maximale pour les questions difficiles',
      'eu.anthropic.claude-opus-4-7': 'Idéal pour les analyses approfondies et les fichiers longs',
      'eu.anthropic.claude-opus-4-6-v1':
        'Idéal pour les analyses approfondies et les fichiers longs',
      'eu.anthropic.claude-opus-4-5-20251101-v1:0':
        'Idéal pour les analyses approfondies et les fichiers longs',
      'eu.anthropic.claude-sonnet-4-6': 'Idéal pour la rédaction, les résumés et les modifications'
    }
  })

  const props = defineProps<{
    modelId: string | null
    models: AvailableModel[]
    disabled?: boolean
    chatContext: 'case' | 'general'
  }>()

  const { trackEvent } = useMetrics()

  const emit = defineEmits<{
    (e: 'update:modelId', value: string | null): void
  }>()

  const isOpen = ref(false)

  // Double-gate the selector's visibility:
  //   1. PostHog CLAUDE_MODELS_ENABLED flag must be on for the current user, AND
  //   2. the backend `available_models` response must include models from more
  //      than one provider.
  //
  // The client-side flag check protects us when the backend hasn't deployed the
  // latest filter logic yet (e.g. a staging env still on master while the
  // feature lives on a topic branch): even if the backend leaks Claude rows
  // into the list, the selector stays hidden.
  const claudeModelsFlag = useFeatureFlag('CLAUDE_MODELS_ENABLED')
  onMounted(() => {
    void claudeModelsFlag.checkFlag()
  })

  const isVisible = computed(() => {
    if (!claudeModelsFlag.isEnabled.value) return false
    const providers = new Set(props.models.map((m) => m.provider))
    return providers.size > 1
  })

  // If the selector hides while a non-Auto model is selected (flag flipped
  // off, or backend stopped returning Claude), clear the parent's selection
  // so the next send resolves via Auto instead of hitting ModelUnavailableError
  // on an invisible choice. `immediate: true` handles the mount-already-hidden
  // case where isVisible never transitions — e.g. a persisted Claude modelId
  // on a user whose flag is already off on first mount.
  watch(
    isVisible,
    (visible) => {
      if (!visible && props.modelId !== null) {
        emit('update:modelId', null)
      }
    },
    { immediate: true }
  )

  const selectedModel = computed(
    () => props.models.find((m) => m.model_id === props.modelId) ?? null
  )

  const displayLabel = computed(() => {
    if (!props.modelId || !selectedModel.value) return t('auto')
    return selectedModel.value.display_label
  })

  const providerLogo = (provider: string): string | null => {
    if (provider === 'anthropic') return anthropicLogo
    if (provider === 'openai') return chatgptLogo
    return null
  }

  const triggerLogo = computed(() => {
    if (!selectedModel.value) return null
    return providerLogo(selectedModel.value.provider)
  })

  const getDescription = (model: AvailableModel): string => {
    // i18n wins so DE/FR users see localised descriptions. The DB-backed
    // `model.description` (English seed) stays as a safety net for models an
    // admin adds via Django admin without a matching FE translation entry.
    const translated = t(model.model_id)
    if (translated && translated !== model.model_id) return translated
    return model.description ?? ''
  }

  const selectModel = (modelId: string | null) => {
    // No-op when the user clicks the already-selected option — prevents spammy
    // events on a re-click and keeps `previous_model` semantically meaningful.
    // The `isVisible → false` auto-reset path emits through the watch above
    // (not this function), so system-driven nulls never reach analytics.
    if (modelId === props.modelId) {
      isOpen.value = false
      return
    }
    // `actual_model` intentionally equals `model` at FE emit time: the selector
    // fires on user intent, before the BE resolver runs. Dashboards should join
    // to `ai_chat_message_sent.effective_model` for the post-resolution model.
    // See AI-432 dashboard spec.
    void trackEvent(EventNameEnum.AI_CHAT_MODEL_SELECTED, {
      model: modelId,
      actual_model: modelId,
      previous_model: props.modelId,
      chat_context: props.chatContext
    })
    emit('update:modelId', modelId)
    isOpen.value = false
  }
</script>

<template>
  <Popover
    v-if="isVisible"
    v-model:open="isOpen"
  >
    <PopoverTrigger
      as-child
      :disabled="disabled"
    >
      <button
        type="button"
        class="flex items-center gap-1 px-3 py-1.5 disabled:opacity-50"
        :disabled="disabled"
      >
        <img
          v-if="triggerLogo"
          class="h-4 w-4 object-contain"
          :src="triggerLogo"
          alt=""
        />
        <span class="text-sm font-normal leading-5 text-gray-600 font-inter">
          {{ displayLabel }}
        </span>
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      side="bottom"
      :side-offset="8"
      class="z-50 min-h-[52px] w-80 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg"
    >
      <!-- Auto option -->
      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-md px-3 py-1.5 hover:bg-accent"
        @click="selectModel(null)"
      >
        <div class="flex min-w-0 flex-1 flex-col items-start text-left">
          <span class="text-sm font-medium leading-5 text-gray-800 font-inter">
            {{ t('auto') }}
          </span>
          <span class="text-sm font-normal leading-5 text-gray-500 font-inter">
            {{ t('autoDesc') }}
          </span>
        </div>
        <Check
          v-if="!modelId"
          class="h-4 w-4 shrink-0 text-gray-800"
        />
      </button>

      <!-- Model options -->
      <button
        v-for="model in models"
        :key="model.model_id"
        type="button"
        class="flex w-full items-center gap-3 rounded-md px-3 py-1.5 hover:bg-accent"
        @click="selectModel(model.model_id)"
      >
        <div class="flex min-w-0 flex-1 flex-col items-start text-left">
          <div class="flex items-center gap-1.5">
            <span class="text-sm font-medium leading-5 text-gray-800 font-inter">
              {{ model.display_label }}
            </span>
            <span
              v-if="getVersion(model.model_id)"
              class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal leading-4 text-gray-600 font-inter"
            >
              {{ getVersion(model.model_id) }}
            </span>
          </div>
          <span
            v-if="getDescription(model)"
            class="text-sm font-normal leading-5 text-gray-500 font-inter"
          >
            {{ getDescription(model) }}
          </span>
        </div>
        <Check
          v-if="modelId === model.model_id"
          class="h-4 w-4 shrink-0 text-gray-800"
        />
      </button>
    </PopoverContent>
  </Popover>
</template>
